// The Classical style's tunes (docs/AUDIO_DESIGN.md §12): public-domain themes by Mozart and Beethoven, arranged for a
// lofi band. Each piece has two 8-bar parts in 4/4, `a` (the theme: the A sections and the break) and `b` (its second
// strain or a reharmonized restatement: the B sections), written in a reference key and transposed to the track's.
//   tonic, mode   the reference key (pitch class, 'major' | 'minor'); a track plays the pieces of its key's mode
//   comp          the comping cells' weights (classical.js COMPS): the piece's own accompaniment figure
//   ceil, swing, groove   optional: the chords' highest note (reference key) when they must stay under a low tune, a
//                 swing (50 = straight, for triplets) and a drum grid id in place of the track's own
//   a, b          { pickup, bars, chords }:
//     pickup      notes before the part's first downbeat, sounding at the end of the bar before it (whatever plays there)
//     bars        8 bars of 'NOTE:steps' tokens (16th steps, each bar sums to 16; r = a rest; NOTE:steps~ring holds a note
//                 past its slot, e.g. into the next bar), or 'a' to restate the theme's bars over new chords
//     chords      [semitones from the tonic, quality, beats] (harmony.js format), 32 beats
// Tunes in 3/8 or 2/4 are re-barred: two of their bars make one 4/4 bar (Für Elise's first note of each 3/8 bar takes the
// extra steps); a slow 2/4 (the Pathétique) doubles its note values instead, one of its bars to a 4/4 bar. Checked against
// the Mutopia Project and Humdrum (craigsapp) scores; Für Elise's second strain skips its two-bar trill back to the tune.

/* ---------------- notes ---------------- */
const PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
/** 'D#5' → MIDI 75 */
export function midiOf(name) {
  const m = /^([A-G])(#|b)?(\d)$/.exec(name);
  if (!m) throw new Error(`classical: bad note ${name}`);
  return 12 * (+m[3] + 1) + PC[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
}
/** 'E5:4 r:2 G4:2~6' → [{ s, d, m }] from step `at` (rests advance the step and leave no note); `total` = steps covered */
export function parseBar(str, at = 0) {
  const out = [];
  let s = at;
  for (const tok of str.trim().split(/\s+/)) {
    const [name, len] = tok.split(':'), [step, ring] = len.split('~').map(Number);
    if (!(step > 0)) throw new Error(`classical: bad length in ${tok}`);
    if (name !== 'r') out.push({ s, d: ring || step, m: midiOf(name) });
    s += step;
  }
  out.total = s - at;
  return out;
}
/** a part's pickup: its notes end on the downbeat (negative steps) */
const parsePickup = str => {
  if (!str) return [];
  const n = parseBar(str);
  return n.map(x => ({ ...x, s: x.s - n.total }));
};

/* ---------------- the pieces ---------------- */
const RAW = {
  ode: {
    name: 'Ode to Joy', by: 'Beethoven', tonic: 0, mode: 'major',
    comp: { broken: 1, pulse: 0.6, hold: 0.35 },
    a: {
      bars: [
        'E5:4 E5:4 F5:4 G5:4', 'G5:4 F5:4 E5:4 D5:4', 'C5:4 C5:4 D5:4 E5:4', 'E5:6 D5:2 D5:8',
        'E5:4 E5:4 F5:4 G5:4', 'G5:4 F5:4 E5:4 D5:4', 'C5:4 C5:4 D5:4 E5:4', 'D5:6 C5:2 C5:8',
      ],
      chords: [[0, 'maj7', 4], [7, '7', 4], [9, 'm7', 4], [2, 'm7', 2], [7, '7', 2],
        [0, 'maj7', 4], [7, '7', 4], [5, 'maj7', 2], [4, 'm7', 2], [7, '7sus4', 2], [0, 'maj9', 2]],
    },
    b: {       // the middle strain (the 12th bar's last E anticipates the 13th's downbeat), then the tune again
      bars: [
        'D5:4 D5:4 E5:4 C5:4', 'D5:4 E5:2 F5:2 E5:4 C5:4', 'D5:4 E5:2 F5:2 E5:4 D5:4', 'C5:4 D5:4 G4:4 E5:4~8',
        'r:4 E5:4 F5:4 G5:4', 'G5:4 F5:4 E5:4 D5:4', 'C5:4 C5:4 D5:4 E5:4', 'D5:6 C5:2 C5:8',
      ],
      chords: [[7, '7', 4], [7, '7', 2], [0, 'maj7', 2], [2, 'm7', 2], [7, '7', 2], [0, 'add9', 2], [7, '7', 2],
        [0, 'maj7', 4], [7, '7', 4], [5, 'maj7', 2], [4, 'm7', 2], [7, '7sus4', 2], [0, 'maj9', 2]],
    },
  },
  twinkle: {
    name: 'Twinkle Variations', by: 'Mozart', tonic: 0, mode: 'major',      // "Ah vous dirai-je, maman", K. 265
    comp: { alberti: 1, broken: 0.5, hold: 0.25 },
    a: {
      bars: [
        'C5:4 C5:4 G5:4 G5:4', 'A5:4 A5:4 G5:8', 'F5:4 F5:4 E5:4 E5:4', 'D5:4 D5:4 C5:8',
        'C5:4 C5:4 G5:4 G5:4', 'A5:4 A5:4 G5:8', 'F5:4 F5:4 E5:4 E5:4', 'D5:4 D5:4 C5:8',
      ],
      chords: [[0, 'maj7', 4], [5, 'maj7', 2], [0, 'maj7', 2], [2, 'm7', 2], [0, 'maj7', 2], [7, '7', 2], [0, 'add9', 2],
        [0, 'maj7', 4], [5, 'maj7', 2], [0, 'maj7', 2], [2, 'm7', 2], [9, 'm7', 2], [7, '7', 2], [0, 'add9', 2]],
    },
    b: {       // the middle strain twice, then the tune
      bars: [
        'G5:4 G5:4 F5:4 F5:4', 'E5:4 E5:4 D5:8', 'G5:4 G5:4 F5:4 F5:4', 'E5:4 E5:4 D5:8',
        'C5:4 C5:4 G5:4 G5:4', 'A5:4 A5:4 G5:8', 'F5:4 F5:4 E5:4 E5:4', 'D5:4 D5:4 C5:8',
      ],
      chords: [[4, 'm7', 2], [2, 'm7', 2], [0, 'maj7', 2], [7, '7', 2], [4, 'm7', 2], [2, 'm7', 2], [9, 'm7', 2], [7, '7sus4', 2],
        [0, 'maj7', 4], [5, 'maj7', 2], [0, 'maj7', 2], [2, 'm7', 2], [0, 'maj7', 2], [7, '7', 2], [0, 'add9', 2]],
    },
  },
  elise: {
    name: 'Für Elise', by: 'Beethoven', tonic: 9, mode: 'minor',
    comp: { arp: 1, hold: 0.3, pulse: 0.2 },
    a: {
      pickup: 'E5:1 D#5:1',
      bars: [
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 G#4:1 B4:1 C5:5 E4:1 E5:1 D#5:1',
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 C5:1 B4:1 A4:6 E5:1 D#5:1',
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 G#4:1 B4:1 C5:5 E4:1 E5:1 D#5:1',
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 C5:1 B4:1 A4:8',
      ],
      chords: [[0, 'm7', 4], [7, '7', 2], [0, 'm7', 2], [0, 'm7', 4], [7, '7', 2], [0, 'm7', 2],
        [0, 'm7', 4], [7, '7', 2], [0, 'm7', 2], [0, 'm7', 4], [7, '7', 2], [0, 'm7', 2]],
    },
    b: {       // the second strain, twice
      pickup: 'B4:1 C5:1 D5:1',
      bars: [
        'E5:5 G4:1 F5:1 E5:1 D5:5 F4:1 E5:1 D5:1', 'C5:5 E4:1 D5:1 C5:1 B4:5 E4:1 E5:1 D#5:1',
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 C5:1 B4:1 A4:5 B4:1 C5:1 D5:1',
        'E5:5 G4:1 F5:1 E5:1 D5:5 F4:1 E5:1 D5:1', 'C5:5 E4:1 D5:1 C5:1 B4:5 E4:1 E5:1 D#5:1',
        'E5:1 D#5:1 E5:1 B4:1 D5:1 C5:3 A4:5 C4:1 E4:1 A4:1', 'B4:5 E4:1 C5:1 B4:1 A4:8',
      ],
      chords: [[3, 'maj7', 2], [10, '7', 2], [0, 'm7', 2], [7, '7', 2], [0, 'm7', 4], [7, '7', 2], [0, 'm7', 2],
        [3, 'maj7', 2], [10, '7', 2], [0, 'm7', 2], [7, '7', 2], [0, 'm7', 4], [7, '7', 2], [0, 'm7', 2]],
    },
  },
  moonlight: {
    name: 'Moonlight Sonata', by: 'Beethoven', tonic: 1, mode: 'minor',     // Op. 27 No. 2, I: bars 1–15
    comp: { triplets: 1 }, ceil: 66, swing: 50, groove: 'P5',
    a: {       // four bars of triplets, then the tune comes in on the last beat
      bars: [
        'r:16', 'r:16', 'r:16', 'r:16',
        'r:12 G#4:3 G#4:1', 'G#4:12 G#4:3 G#4:1', 'G#4:8 A4:8', 'G#4:8 F#4:4 B4:4',
      ],
      chords: [[0, 'min', 4], [0, 'm7', 4], [8, 'maj', 2], [1, 'maj', 2], [7, '7', 1], [0, 'min', 1], [7, '7sus4', 1], [7, '7', 1],
        [0, 'min', 4], [7, '7', 4], [0, 'min', 2], [5, 'min', 2], [3, 'maj', 2], [10, '7', 2]],
    },
    b: {       // bars 9–15 (E major, E minor, round to B), then the dominant back to C♯ minor
      bars: [
        'E4:4 r:12', 'r:12 G4:3 G4:1', 'G4:12 G4:3 G4:1', 'G4:12 F#4:4',
        'F#4:8 G4:4 E4:4', 'F#4:8 F#4:8', 'B3:4 r:8 B4:4', 'r:16',
      ],
      chords: [[3, 'maj', 4], [3, 'min', 4], [6, '7', 4], [11, 'maj', 1], [3, 'min', 1], [9, 'dim7', 1], [5, '7', 1],
        [10, 'min', 2], [3, 'm6', 2], [10, 'min', 2], [5, 'maj', 2], [10, 'min', 2], [10, 'maj', 2], [7, '7', 4]],
    },
  },
  fifth: {
    name: 'Symphony No. 5', by: 'Beethoven', tonic: 0, mode: 'minor',       // I: the motto, then the strings' answers
    comp: { hold: 1, throb: 0.5, pulse: 0.4 },
    a: {
      bars: [
        'r:2 G4:2 G4:2 G4:2 Eb4:8', 'r:2 F4:2 F4:2 F4:2 D4:8', 'r:2 G4:2 G4:2 G4:2 Eb4:8', 'r:2 Ab4:2 Ab4:2 Ab4:2 G4:8',
        'r:2 Eb5:2 Eb5:2 Eb5:2 C5:8', 'r:2 F5:2 F5:2 F5:2 D5:8', 'D5:2 G5:2 G5:2 F5:2 Eb5:8', 'D5:2 G5:2 G5:2 F5:2 Eb5:4 r:4',
      ],
      chords: [[0, 'm7', 4], [7, '7', 4], [0, 'm9', 4], [5, 'm7', 2], [0, 'min', 2],
        [8, 'maj7', 4], [7, '7', 4], [7, '7', 2], [0, 'min', 2], [7, '7', 2], [0, 'm9', 2]],
    },
    b: {       // the motto again over lofi chords
      bars: 'a',
      chords: [[8, 'maj7', 4], [2, 'm7b5', 4], [0, 'm9', 4], [5, 'm9', 2], [0, 'm7', 2],
        [9, 'm7b5', 4], [7, '7sus4', 2], [7, '7', 2], [7, '7', 2], [0, 'm9', 2], [7, '7', 2], [0, 'm9', 2]],
    },
  },
  pathetique: {
    name: 'Pathétique', by: 'Beethoven', tonic: 8, mode: 'major',           // Op. 13, II: the Adagio's theme, as restated an octave up
    comp: { murmur: 1, hold: 0.3 },
    a: {
      bars: [
        'C5:8 Bb4:8', 'Eb5:12 Db5:4', 'C5:4 Eb5:4 Ab5:4 Bb5:4', 'Eb5:12 E5:4',
        'F5:8 Bb4:6 C5:1 Db5:1', 'Eb5:8 A4:8', 'Db5:8 C5:2 Bb4:2 Ab4:2 G4:2', 'Bb4:8 Ab4:4 r:4',
      ],
      chords: [[0, 'maj7', 2], [7, '7', 2], [0, 'maj', 2], [7, '7', 2], [0, 'maj', 1], [7, 'maj', 1], [9, 'min', 1], [2, '7', 1],
        [7, 'add9', 4], [11, 'm7b5', 2], [7, '7', 2], [0, 'maj', 2], [9, '7', 2], [2, 'm7', 2], [7, '7', 2], [7, '7', 2], [0, 'maj7', 2]],
    },
    b: {       // the tune again on fuller chords
      bars: 'a',
      chords: [[0, 'maj9', 2], [7, '7sus4', 2], [5, 'm7', 2], [7, '7', 2], [0, 'maj7', 1], [7, '7', 1], [9, 'm7', 1], [2, 'm7', 1],
        [7, 'add9', 4], [2, 'm7', 2], [7, '7', 2], [5, 'm7', 2], [9, '7', 2], [2, 'm7', 2], [7, '7', 2], [7, '7', 2], [0, 'maj9', 2]],
    },
  },
  nachtmusik: {
    name: 'Eine kleine Nachtmusik', by: 'Mozart', tonic: 7, mode: 'major',  // K. 525, I: bars 1–10
    comp: { broken: 1, pulse: 0.6, throb: 0.4 },
    a: {
      bars: [
        'G5:4 r:2 D5:2 G5:4 r:2 D5:2', 'G5:2 D5:2 G5:2 B5:2 D6:4 r:4', 'C6:4 r:2 A5:2 C6:4 r:2 A5:2', 'C6:2 A5:2 F#5:2 A5:2 D5:4 r:4',
        'G5:2 r:2 G5:6 B5:2 A5:2 G5:2', 'G5:2 F#5:2 F#5:6 A5:2 C6:2 F#5:2', 'A5:2 G5:2 G5:6 B5:2 A5:2 G5:2', 'G5:2 F#5:2 F#5:6 A5:2 C6:2 F#5:2',
      ],
      chords: [[0, 'maj', 4], [0, 'maj7', 4], [7, '7', 4], [7, '7', 4],
        [0, 'maj7', 4], [7, '7sus4', 1], [7, '7', 3], [0, 'maj7', 4], [7, '7sus4', 1], [7, '7', 3]],
    },
    b: {       // the cadence (bars 9–10) twice, then the opening call
      bars: [
        'G5:2 G5:2 F#5:2 E5:1 F#5:1 G5:2 G5:2 A5:2 G5:1 A5:1', 'B5:2 B5:2 C6:2 B5:1 C6:1 D6:4 r:4',
        'G5:2 G5:2 F#5:2 E5:1 F#5:1 G5:2 G5:2 A5:2 G5:1 A5:1', 'B5:2 B5:2 C6:2 B5:1 C6:1 D6:4 r:4',
        'G5:4 r:2 D5:2 G5:4 r:2 D5:2', 'G5:2 D5:2 G5:2 B5:2 D6:4 r:4', 'C6:4 r:2 A5:2 C6:4 r:2 A5:2', 'C6:2 A5:2 F#5:2 A5:2 D5:4 r:4',
      ],
      chords: [[0, 'maj', 1], [7, 'maj', 1], [0, 'maj', 1], [7, 'maj', 1], [0, 'maj7', 1], [7, '7', 1], [0, 'maj7', 2],
        [0, 'maj', 1], [7, 'maj', 1], [0, 'maj', 1], [7, 'maj', 1], [0, 'maj7', 1], [7, '7', 1], [0, 'maj7', 2],
        [0, 'maj', 4], [0, 'maj7', 4], [7, '7', 4], [7, '7', 4]],
    },
  },
  forty: {
    name: 'Symphony No. 40', by: 'Mozart', tonic: 7, mode: 'minor',         // K. 550, I: bars 0–19
    comp: { throb: 1, pulse: 0.35 },
    a: {
      pickup: 'Eb5:2 D5:2',
      bars: [
        'D5:4 Eb5:2 D5:2 D5:4 Eb5:2 D5:2', 'D5:4 Bb5:4 r:4 Bb5:2 A5:2', 'G5:4 G5:2 F5:2 Eb5:4 Eb5:2 D5:2', 'C5:4 C5:4 r:4 D5:2 C5:2',
        'C5:4 D5:2 C5:2 C5:4 D5:2 C5:2', 'C5:4 A5:4 r:4 A5:2 G5:2', 'F#5:4 F#5:2 Eb5:2 D5:4 D5:2 C5:2', 'Bb4:4 Bb4:4 r:8',
      ],
      chords: [[0, 'm7', 4], [0, 'm7', 4], [0, 'm7', 4], [2, 'm7b5', 4], [2, 'm7b5', 4], [7, '7', 4], [7, '7', 4], [0, 'm7', 4]],
    },
    b: {       // the consequent: up to the diminished chords, down to the dominant's long D
      pickup: 'Bb5:2 A5:2',
      bars: [
        'A5:4 C6:4 F#5:4 A5:4', 'G5:4 D5:4 r:4 Bb5:2 A5:2', 'A5:4 C6:4 F#5:4 A5:4', 'G5:4 Bb5:4 A5:2 G5:2 F5:2 Eb5:2',
        'D5:16', 'C#5:16', 'D5:8 r:4 D5:2 D5:2', 'D5:8 r:8',
      ],
      chords: [[5, 'dim7', 4], [0, 'm7', 4], [5, 'dim7', 4], [0, 'm7', 4], [9, 'm7b5', 4], [8, '7', 4], [7, '7', 4], [7, '7sus4', 2], [7, '7', 2]],
    },
  },
  turca: {
    name: 'Rondo alla Turca', by: 'Mozart', tonic: 9, mode: 'minor',        // K. 331, III: bars 0–24
    comp: { oompah: 1, pulse: 0.25 },
    a: {       // the A-minor tune with its first ending, then its reprise with the closing one
      pickup: 'B4:1 A4:1 G#4:1 A4:1',
      bars: [
        'C5:2 r:2 D5:1 C5:1 B4:1 C5:1 E5:2 r:2 F5:1 E5:1 D#5:1 E5:1', 'B5:1 A5:1 G#5:1 A5:1 B5:1 A5:1 G#5:1 A5:1 C6:4 A5:2 C6:2',
        'B5:2 A5:2 G5:2 A5:2 B5:2 A5:2 G5:2 A5:2', 'B5:2 A5:2 G5:2 F#5:2 E5:4 B4:1 A4:1 G#4:1 A4:1',
        'C5:2 r:2 D5:1 C5:1 B4:1 C5:1 E5:2 r:2 F5:1 E5:1 D#5:1 E5:1', 'B5:1 A5:1 G#5:1 A5:1 B5:1 A5:1 G#5:1 A5:1 C6:4 A5:2 B5:2',
        'C6:2 B5:2 A5:2 G#5:2 A5:2 E5:2 F5:2 D5:2', 'C5:4 B4:4 A4:4 r:4',
      ],
      chords: [[0, 'min', 4], [0, 'min', 4], [7, 'm7', 4], [7, 'm7', 2], [7, '7', 2],
        [0, 'min', 4], [0, 'min', 2], [8, '7', 2], [0, 'min', 2], [5, 'm7', 2], [0, 'min', 1], [7, '7', 1], [0, 'm7', 2]],
    },
    b: {       // the C-major strain, twice
      pickup: 'E5:2 F5:2',
      bars: [
        'G5:2 G5:2 A5:1 G5:1 F5:1 E5:1 D5:4 E5:2 F5:2', 'G5:2 G5:2 A5:1 G5:1 F5:1 E5:1 D5:4 C5:2 D5:2',
        'E5:2 E5:2 F5:1 E5:1 D5:1 C5:1 B4:4 C5:2 D5:2', 'E5:2 E5:2 F5:1 E5:1 D5:1 C5:1 B4:4 E5:2 F5:2',
        'G5:2 G5:2 A5:1 G5:1 F5:1 E5:1 D5:4 E5:2 F5:2', 'G5:2 G5:2 A5:1 G5:1 F5:1 E5:1 D5:4 C5:2 D5:2',
        'E5:2 E5:2 F5:1 E5:1 D5:1 C5:1 B4:4 C5:2 D5:2', 'E5:2 E5:2 F5:1 E5:1 D5:1 C5:1 B4:4 r:4',
      ],
      chords: [[3, 'maj7', 2], [10, '7', 2], [3, 'maj7', 2], [10, '7', 2], [0, 'm7', 2], [7, '7', 2], [0, 'm7', 2], [7, '7', 2],
        [3, 'maj7', 2], [10, '7', 2], [3, 'maj7', 2], [10, '7', 2], [0, 'm7', 2], [7, '7', 2], [0, 'm7', 2], [7, '7', 2]],
    },
  },
  facile: {
    name: 'Sonata facile', by: 'Mozart', tonic: 0, mode: 'major',           // K. 545, I: bars 1–12
    comp: { alberti: 1, broken: 0.2 },
    a: {
      bars: [
        'C5:8 E5:4 G5:4', 'B4:6 C5:1 D5:1 C5:4 r:4', 'A5:8 G5:4 C6:4', 'G5:4 F5:2 E5:1 F5:1 E5:4 r:4',
        'A4:2 B4:1 C5:1 D5:1 E5:1 F5:1 G5:1 A5:1 G5:1 F5:1 E5:1 D5:1 C5:1 B4:1 A4:1',
        'G4:2 A4:1 B4:1 C5:1 D5:1 E5:1 F5:1 G5:1 F5:1 E5:1 D5:1 C5:1 B4:1 A4:1 G4:1',
        'F4:2 G4:1 A4:1 B4:1 C5:1 D5:1 E5:1 F5:1 E5:1 D5:1 C5:1 B4:1 A4:1 G4:1 F4:1',
        'E4:2 F4:1 G4:1 A4:1 B4:1 C5:1 D5:1 E5:1 D5:1 C5:1 B4:1 A4:1 G4:1 F4:1 E4:1',
      ],
      chords: [[0, 'maj7', 4], [7, '7', 2], [0, 'maj', 2], [5, 'maj7', 2], [0, 'maj', 2], [7, 'maj', 2], [0, 'maj', 2],
        [5, 'maj7', 4], [0, 'maj7', 4], [2, 'm7', 2], [7, '7', 2], [0, 'maj', 4]],
    },
    b: {       // the runs up to the half cadence (bars 9–12), then the opening again
      bars: [
        'D4:2 E4:1 F4:1 G4:1 A4:1 B4:1 C#5:1 D5:1 A4:1 B4:1 C#5:1 D5:1 E5:1 F5:1 G5:1',
        'A5:1 B5:1 C6:1 B5:1 A5:1 G5:1 F5:1 E5:1 F5:1 G5:1 A5:1 G5:1 F5:1 E5:1 D5:1 C5:1',
        'B4:2 G5:2 E5:2 C5:2 D5:2 G5:2 E5:2 C5:2', 'D5:4 G5:4 G4:4 r:4',
        'C5:8 E5:4 G5:4', 'B4:6 C5:1 D5:1 C5:4 r:4', 'A5:8 G5:4 C6:4', 'G5:4 F5:2 E5:1 F5:1 E5:4 r:4',
      ],
      chords: [[2, 'min', 4], [5, 'maj', 1], [7, 'maj', 1], [2, 'min', 1], [2, '7', 1], [7, 'maj', 2], [0, 'maj', 2], [7, '7', 4],
        [0, 'maj7', 4], [7, '7', 2], [0, 'maj', 2], [5, 'maj7', 2], [0, 'maj', 2], [7, 'maj', 2], [0, 'maj', 2]],
    },
  },
};

/* ---------------- parsed ---------------- */
function part(raw, theme) {
  const bars = (raw.bars === 'a' ? theme.bars : raw.bars).map(str => parseBar(str));
  return { pickup: parsePickup(raw.bars === 'a' ? theme.pickup : raw.pickup), bars, chords: raw.chords };
}
/** id → { name, by, tonic, mode, comp, a: { pickup, bars: [[{ s, d, m }]], chords }, b, lo, hi } */
export const PIECES = Object.fromEntries(Object.entries(RAW).map(([id, p]) => {
  const a = part(p.a, p.a), b = part(p.b, p.a);
  const all = [a, b].flatMap(x => [...x.pickup, ...x.bars.flat()]).map(n => n.m);
  return [id, { ...p, a, b, lo: Math.min(...all), hi: Math.max(...all) }];
}));
/** the raw bar strings (tests: every bar sums to 16 steps) */
export const SCORES = RAW;
